<template>
  <div
    id="eocs-filter-container"
    class="filters-container"
    :class="loading ? 'disabled' : ''"
  >
    <div
      class="d-flex flex-row justify-content-between align-items-center bg-light-gray min-width-fit-content"
    >
      <div class="d-flex flex-wrap align-items-center">
        <b-form-input
          class="auth-input search-input border-color-light-gray"
          v-model="searchPhrase"
          placeholder="Search patient..."
          :readonly="loading"
        />
        <span class="ml-4 mr-3" style="font-size: 14px;">Active filters:</span>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pS, pSIndex) in eocStatus"
          :key="pS.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Status: <span class="color-filter">{{ pS.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('eocStatus', pSIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(loc, locIndex) in levelOfCare"
          :key="loc.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Level of care:
              <span class="color-filter">{{ loc.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('levelOfCareId', locIndex)"
            ></i>
          </div>
        </div>
        <div
          v-if="dateOfBirthFrom || dateOfBirthTo"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Date of birth:
              <span class="color-filter">{{
                dateOfBirthFrom || dateOfBirthTo
                  ? `(${
                      dateOfBirthFrom
                        ? "From: " + formatDateOnly(dateOfBirthFrom)
                        : ""
                    }${dateOfBirthFrom && dateOfBirthTo ? " - " : ""}${
                      dateOfBirthTo
                        ? "To: " + formatDateOnly(dateOfBirthTo)
                        : ""
                    })`
                  : ""
              }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeDateOfBirthFilter()"
            ></i>
          </div>
        </div>
        <div
          v-if="startDateFrom || startDateTo"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Start/Admission date:
              <span class="color-filter">{{
                startDateFrom || startDateTo
                  ? `(${
                      startDateFrom
                        ? "From: " + formatDateOnly(startDateFrom)
                        : ""
                    }${startDateFrom && startDateTo ? " - " : ""}${
                      startDateTo ? "To: " + formatDateOnly(startDateTo) : ""
                    })`
                  : ""
              }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeStartDateFilter()"
            ></i>
          </div>
        </div>
        <div
          v-if="endDateFrom || endDateTo"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >End date:
              <span class="color-filter"
                >{{
                  endDateFrom || endDateTo
                    ? `(${
                        endDateFrom
                          ? "From: " + formatDateOnly(endDateFrom)
                          : ""
                      }${endDateFrom && endDateTo ? " - " : ""}${
                        endDateTo ? "To: " + formatDateOnly(endDateTo) : ""
                      })`
                    : ""
                }}
              </span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeEndDateFilter()"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(option, index) in assignedTo"
          :key="option.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3">
              Assigned To:
              <span class="color-filter">{{ option.label }}</span>
            </span>
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('assignedTo', index)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(option, index) in dischargeReason"
          :key="option.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3">
              Discharge Reason:
              <span class="color-filter">{{ option.label }}</span>
            </span>
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('dischargeReasonId', index)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(badge, badgeIndex) in badge"
          :key="badge.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3 d-flex align-items-center"
              >{{ badge.value !== true ? "Badge" : "Alerts" }}:
              <span
                v-if="badge.value !== true"
                class="badge text-white ml-1"
                :style="{
                  backgroundColor: badge.color
                }"
              >
                {{ badge.acronym }}
              </span>
              <span class="ml-1" v-else>
                <i
                  class="alert-note-icon fas fa-exclamation-circle text-danger"
                /> </span
            ></span>
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('badgeId', badgeIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pS, pSIndex) in locations"
          :key="pS.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Locations: <span class="color-filter">{{ pS.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('locations', pSIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pS, pSIndex) in placesOfService"
          :key="pS.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Places of Service:
              <span class="color-filter">{{ pS.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('placesOfService', pSIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(c, cIndex) in contact"
          :key="c.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Contact: <span class="color-filter">{{ c.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('contactId', cIndex)"
            ></i>
          </div>
        </div>
        <span
          id="eocs-filters-clear-all"
          v-if="hasFilters"
          class="clear-all-filters text-danger ml-2"
          style="font-size: 14px;"
          @click="clearAllFilters"
          >Clear all filters</span
        >
      </div>
      <div class="d-flex justify-content-end">
        <div class="d-flex align-self-stretch align-items-center">
          <div class="align-self-stretch" style="padding: 9px 0px;">
            <div class="vertical-separator border-med-light-gray"></div>
          </div>
          <b-dropdown
            id="eocs-filter-dropdown"
            text="Filters"
            ref="filterDropdown"
            class="container-main bg-light-gray"
            toggle-class="d-flex flex-row justify-content-between align-items-lg-center bg-transparent border-0 color-custom-dark-grey dropdown-menu-label"
            menu-class="container-filter-main-menu"
            toggle-tag="div"
            boundary="window"
            right
          >
            <template #button-content>
              <div class="d-flex align-items-center mr-2">
                <i
                  class="fas fa-filter text-primary mr-2"
                  style="font-size: 15px;"
                  aria-hidden="true"
                ></i>
                <span style="font-size: 14px;">Filters</span>
              </div>
            </template>
            <b-dropdown-form class="px-2">
              <b-form-group label="Filter by:">
                <b-form-select
                  id="eocs-filter-picker"
                  v-model="selectedFilter"
                  :options="
                    filterOptionsAccess.filter(
                      f => !this.selectedLocation || f.value != 'locations'
                    )
                  "
                />
              </b-form-group>

              <multiple-select-filter
                v-if="selectedFilter == 'assignedTo'"
                :label="'Assigned To'"
                :options="allUsers"
                v-model="assignedTo"
                :width="'250px'"
                :showSearch="false"
                :key="'assignedTo'"
                :id="'eocsIndexFilters-assignedTo'"
              />
              <multiple-select-filter
                v-if="selectedFilter == 'status'"
                :label="'Status'"
                :options="eocStatuses"
                v-model="eocStatus"
                :width="'250px'"
                :showSearch="false"
                :key="'status'"
                :id="'eocsIndexFilters-status'"
              />

              <multiple-select-filter
                v-if="selectedFilter == 'levelOfCare'"
                :label="'Current LOC'"
                :options="levelsOfCare"
                v-model="levelOfCare"
                :width="'250px'"
                :showSearch="false"
                :key="'levelOfCare'"
                :id="'eocsIndexFilters-levelOfCare'"
              />
              <date-range-filter
                v-if="selectedFilter == 'dateOfBirth'"
                v-model="filterDateOfBirthRange"
                :filterWidth="'250px'"
                label="Date of Birth"
                :key="'dateOfBirth'"
                :id="'eocsIndexFilters-dateOfBirth'"
              />
              <date-range-filter
                v-if="selectedFilter == 'startDate'"
                v-model="filterStartDateRange"
                :filterWidth="'250px'"
                label="Start/Admission Date"
                :key="'startDate'"
                :id="'eocsIndexFilters-startDate'"
              />
              <date-range-filter
                v-if="selectedFilter == 'endDate'"
                v-model="filterEndDateRange"
                :filterWidth="'250px'"
                label="End Date"
                :key="'endDate'"
                :id="'eocsIndexFilters-endDate'"
              />
              <multiple-select-filter
                v-if="selectedFilter == 'alertsBadges'"
                :label="'Badges and alerts'"
                :options="badges"
                v-model="badge"
                :width="'250px'"
                :showSearch="false"
                :key="'alertsBadges'"
                :id="'eocsIndexFilters-alertsBadges'"
              >
                <template v-slot:item-option="{ itemOption, selected }">
                  <div class="pl-3 pr-3 pt-1 pb-1 ">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div class="d-flex align-items-center" id="eoc">
                        <div class="text-center mr-2" style="width: 40px">
                          <div
                            v-if="itemOption.acronym"
                            class="badge text-white"
                            :style="{ backgroundColor: itemOption.color }"
                          >
                            {{ itemOption.acronym }}
                          </div>
                          <div v-else>
                            <i
                              class="alert-note-icon fas fa-exclamation-circle text-danger"
                            />
                          </div>
                        </div>
                        {{ itemOption.label }}
                      </div>
                      <img
                        v-if="selected"
                        :src="require('../../assets/icons/check.png')"
                      />
                    </div>
                  </div>
                </template>
              </multiple-select-filter>
              <multiple-select-filter
                v-if="selectedFilter == 'dischargeReason'"
                :label="'Discharge Reason'"
                :options="dischargeReasons"
                v-model="dischargeReason"
                :width="'250px'"
                :showSearch="false"
                :key="'dischargeReason'"
                :id="'eocsIndexFilters-dischargeReason'"
              />
              <multiple-select-filter
                v-if="selectedFilter == 'locations' && !selectedLocation"
                :label="'Locations'"
                :options="locationsOptions"
                v-model="locations"
                :width="'250px'"
                :showSearch="false"
                :key="'status'"
                :id="'eocsIndexFilters-locations'"
              />
              <multiple-select-filter
                v-if="selectedFilter == 'placesOfService'"
                :label="'Places of Service'"
                :options="placesOfServiceOptions"
                v-model="placesOfService"
                :width="'250px'"
                :showSearch="false"
                :key="'placesOfService'"
                :id="'eocsIndexFilters-placesOfService'"
              />
              <multiple-select-filter
                v-if="selectedFilter == 'contacts'"
                :label="'Contacts'"
                :options="contactOptions"
                v-model="contact"
                :width="'250px'"
                :showSearch="true"
                :searching="searchingContacts"
                @on-search="searchContacts"
                :key="'contacts'"
                :id="'eocsIndexFilters-contacts'"
              />
            </b-dropdown-form>
          </b-dropdown>
        </div>
        <div class="d-flex align-self-stretch align-items-center mr-2">
          <div class="align-self-stretch" style="padding: 9px 0px;">
            <div class="vertical-separator border-med-light-gray"></div>
          </div>
          <b-dropdown
            id="order-dropdown-form"
            text="Order by"
            ref="orderDropdown"
            class="container-main bg-light-gray"
            toggle-class="d-flex flex-row justify-content-between align-items-lg-center bg-transparent border-0 color-custom-dark-grey dropdown-menu-label"
            menu-class="container-filter-main-menu"
            toggle-tag="div"
            boundary="window"
            right
          >
            <template #button-content>
              <div class="d-flex align-items-center mr-2">
                <i
                  class="fas fa-sort text-primary mr-2"
                  style="font-size: 15px;"
                  aria-hidden="true"
                ></i>
                <span style="font-size: 14px;">
                  Order by: <strong>{{ orderByText }}</strong>
                </span>
              </div>
            </template>
            <b-dropdown-item-button
              v-for="(item, index) in orderOptions"
              :key="index"
              @click="orderBy = item.value"
            >
              {{ item.text }}
            </b-dropdown-item-button>
          </b-dropdown>
          <button
            id="sorting-order-btn"
            class="sorting-order-btn"
            @click="orderByDescending = !orderByDescending"
          >
            <i :class="sortingOrderClass"></i>
          </button>
          <b-tooltip target="sorting-order-btn" triggers="hover">
            {{ orderByDescending ? "DESC" : "ASC" }}
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import badgeServer from "../../server/badge";
import userServer from "../../server/user";
import dischargeReasonServer from "../../server/dischargeReason";
import contactServer from "../../server/patientContacts";

export default {
  name: "EocsIndexFilters",
  props: ["value", "hasFilters", "loading", "showing"],
  data() {
    return {
      selectedFilter: "status",
      badges: [],
      orderOptions: [
        {
          value: "name",
          text: "Name"
        },
        {
          value: "dateOfBirth",
          text: "Date of birth"
        },
        {
          value: "mrn",
          text: "MRN"
        },
        {
          value: "status",
          text: "Status"
        },
        {
          value: "levelOfCare",
          text: "Level of care"
        },
        {
          value: "startDate",
          text: "Start/Admission date"
        },
        {
          value: "endDate",
          text: "End date"
        },
        {
          value: "alerts",
          text: "Badges & Alerts"
        },
        {
          value: "dischargeReason",
          text: "Discharge reason"
        }
      ],
      filterOptions: [
        {
          value: "status",
          text: "Status"
        },
        {
          value: "levelOfCare",
          text: "Level of care"
        },
        {
          value: "dateOfBirth",
          text: "Date of birth"
        },
        {
          value: "startDate",
          text: "Start/Admission date"
        },
        {
          value: "endDate",
          text: "End date"
        },
        {
          value: "alertsBadges",
          text: "Badges and alerts"
        },
        {
          value: "assignedTo",
          text: "Assigned To"
        },
        {
          value: "dischargeReason",
          text: "Discharge Reason"
        },
        {
          value: "locations",
          text: "Locations"
        },
        {
          value: "placesOfService",
          text: "Places of Service"
        },
        {
          value: "contacts",
          text: "Contacts"
        }
      ],
      eocStatuses: [
        {
          value: "Pending",
          label: "Pending"
        },
        {
          value: "Active",
          label: "Active"
        },
        {
          value: "Inactive",
          label: "Inactive"
        }
      ],
      allUsers: [],
      dischargeReasons: [],
      contactOptions: [],
      searchingContacts: false
    };
  },
  methods: {
    async fetchUsers() {
      const allUsers = await userServer.getMinimalUsers();

      allUsers.forEach(user => {
        const exists = this.allUsers.some(item => item.value === user.id);
        if (!exists) {
          this.allUsers.push({
            value: user.id,
            label: user.firstName + " " + user.lastName
          });
        }
      });
    },
    async fetchBadges() {
      const badges = await badgeServer.query();

      const staticBadgeExists = this.badges.some(item => item.value === true);
      if (!staticBadgeExists) {
        this.badges.push({
          value: true,
          label: "Alerts"
        });
      }

      badges.forEach(badge => {
        const exists = this.badges.some(item => item.value === badge.id);
        if (!exists) {
          this.badges.push({
            value: badge.id,
            acronym: badge.acronym,
            label: badge.name,
            color: badge.category.color
          });
        }
      });
    },
    async fetchDischargeReasons() {
      const reasons = await dischargeReasonServer.query();
      reasons.forEach(reason => {
        const exists = this.dischargeReasons.some(
          item => item.value === reason.id
        );
        if (!exists) {
          this.dischargeReasons.push({
            value: reason.id,
            label: reason.name
          });
        }
      });
    },
    async searchContacts(search) {
      if (this.lastContactTimeout) {
        clearTimeout(this.lastContactTimeout);
        this.lastContactTimeout = null;
      }
      this.lastContactTimeout = setTimeout(async () => {
        if (search && search.length >= 3) {
          this.searchingContacts = true;
          const contacts = await contactServer.filter(
            { searchPhrase: search, page: 0, pageSize: 50 },
            { "Hide-Loading": true }
          );
          this.contactOptions = contacts.map(c => ({
            value: c.id,
            label:
              c.name + (c.email || c.phone ? ` (${c.email || c.phone})` : "")
          }));
          this.searchingContacts = false;
        } else {
          this.contactOptions = [];
        }
      }, 1000);
    },
    removeIndexFromFilter(filterName, index) {
      const filter = [...this.value[filterName]];
      filter.splice(index, 1);
      this.$emit("input", { ...this.value, [filterName]: filter });
    },
    removeStartDateFilter() {
      this.$emit("input", {
        ...this.value,
        startDateFrom: null,
        startDateTo: null
      });
    },
    removeEndDateFilter() {
      this.$emit("input", {
        ...this.value,
        endDateFrom: null,
        endDateTo: null
      });
    },
    removeDateOfBirthFilter() {
      this.$emit("input", {
        ...this.value,
        dateOfBirthFrom: null,
        dateOfBirthTo: null
      });
    },
    clearAllFilters() {
      this.$emit("input", {
        ...this.value,
        eocStatus: [],
        assignedTo: [],
        levelOfCareId: [],
        dischargeReasonId: [],
        badgeId: [],
        contactId: [],
        startDateFrom: null,
        startDateTo: null,
        endDateFrom: null,
        endDateTo: null,
        dateOfBirthFrom: null,
        dateOfBirthTo: null,
        locations: [],
        placesOfService: [],
        contactOptions: []
      });
    }
  },
  computed: {
    ...mapGetters("client", ["levelsOfCareCurrentLocation"]),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      selectedLocation: state => state.selectedLocation
    }),
    ...mapState("client", {
      locationsOptions: state =>
        state.locations.map(location => ({
          value: location.id,
          label: location.name
        })),
      placesOfServiceOptions: state =>
        state.placesOfService.map(place => ({
          value: place.id,
          label: place.name
        }))
    }),
    levelsOfCare() {
      return this.levelsOfCareCurrentLocation.map(levelOfCare => ({
        value: levelOfCare.id,
        label: levelOfCare.name
      }));
    },
    searchPhrase: {
      get() {
        return this.value.searchPhrase;
      },
      set(newValue) {
        this.value.searchPhrase = newValue;
        this.$emit("input", this.value);
      }
    },
    eocStatus: {
      get() {
        return this.value.eocStatus;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, eocStatus: newValue });
      }
    },
    locations: {
      get() {
        return this.value.locations;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, locations: newValue });
      }
    },
    placesOfService: {
      get() {
        return this.value.placesOfService;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, placesOfService: newValue });
      }
    },
    assignedTo: {
      get() {
        return this.value.assignedTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, assignedTo: newValue });
      }
    },
    levelOfCare: {
      get() {
        return this.value.levelOfCareId;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, levelOfCareId: newValue });
      }
    },
    dischargeReason: {
      get() {
        return this.value.dischargeReasonId;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, dischargeReasonId: newValue });
      }
    },
    badge: {
      get() {
        return this.value.badgeId;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, badgeId: newValue });
      }
    },
    contact: {
      get() {
        return this.value.contactId;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, contactId: newValue });
      }
    },
    dateOfBirthFrom: {
      get() {
        return this.value.dateOfBirthFrom;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, dateOfBirthFrom: newValue });
      }
    },
    dateOfBirthTo: {
      get() {
        return this.value.dateOfBirthTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, dateOfBirthTo: newValue });
      }
    },
    filterDateOfBirthRange: {
      get() {
        return {
          dateFrom: this.value.dateOfBirthFrom,
          dateTo: this.value.dateOfBirthTo
        };
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          dateOfBirthFrom: newValue.dateFrom,
          dateOfBirthTo: newValue.dateTo
        });
      }
    },
    startDateFrom: {
      get() {
        return this.value.startDateFrom;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, startDateFrom: newValue });
      }
    },
    startDateTo: {
      get() {
        return this.value.startDateTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, startDateTo: newValue });
      }
    },
    filterStartDateRange: {
      get() {
        return {
          dateFrom: this.value.startDateFrom,
          dateTo: this.value.startDateTo
        };
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          startDateFrom: newValue.dateFrom,
          startDateTo: newValue.dateTo
        });
      }
    },
    endDateFrom: {
      get() {
        return this.value.endDateFrom;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, endDateFrom: newValue });
      }
    },
    endDateTo: {
      get() {
        return this.value.endDateTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, endDateTo: newValue });
      }
    },
    filterEndDateRange: {
      get() {
        return {
          dateFrom: this.value.endDateFrom,
          dateTo: this.value.endDateTo
        };
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          endDateFrom: newValue.dateFrom,
          endDateTo: newValue.dateTo
        });
      }
    },
    orderBy: {
      get() {
        return this.value.orderBy;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, orderBy: newValue });
      }
    },
    orderByText() {
      return this.orderOptions.find(opt => opt.value == this.orderBy).text;
    },
    filterOptionsAccess() {
      return this.currentUser && this.currentUser.onlyAccessAssignedPatients
        ? this.filterOptions.filter(o => o.value != "assignedTo")
        : this.filterOptions;
    },
    orderByDescending: {
      get() {
        return this.value.orderByDescending;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, orderByDescending: newValue });
      }
    },
    sortingOrderClass() {
      return this.value.orderByDescending
        ? "fas fa-sort-amount-down"
        : "fas fa-sort-amount-up";
    }
  },
  watch: {
    showing: {
      handler: async function(value) {
        if (value) {
          await this.fetchBadges();
          await this.fetchUsers();
          await this.fetchDischargeReasons();
        }
      },
      immediate: true
    },
    selectedFilter: function() {
      this.$refs.filterDropdown.updatePopper();
    }
  }
};
</script>

<style scoped>
.filters-container {
  overflow-x: auto;
}
.filters-container >>> .container-filter-main-menu {
  min-width: 250px;
  left: -17px;
}
.filter-value {
  min-width: 100px;
  height: 29px;
  color: #a6a6a6;
  font-size: 14px;
  padding-left: 10px;
}
.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  width: 320px;
}
.color-filter {
  color: #909db8;
}
.clear-all-filters:hover {
  cursor: pointer;
}
.vertical-separator {
  width: 1px;
  border-left: 1px solid;
  height: 100%;
}
div.disabled {
  pointer-events: none;
  opacity: 0.5;
  background: #ccc;
}
.sorting-order-btn {
  background-color: white;
  color: #707070;
  border: 1px solid #dcdcdc;
  border-radius: 3px;
  padding: 1px 4px;
}
</style>
